import { z } from 'zod';

import { BINARY_ANSWERS } from '@/constants/answers';
import { EmirClassification, EmirThreshold, EsmaClassification, NaceClassification } from '@/constants/institution';
import i18n from '@/lib/i18n';
import {
  EMIR_RISK_REDUCTION_CONFIRMATION_OPTIONS,
  institutionDetailsReportingEmirReportingResponsibilityNoSchema,
  institutionDetailsReportingSchema,
} from '@/services/api/profile';

export const institutionFuturesReportingSchema = institutionDetailsReportingSchema;

export type InstitutionFuturesReporting = z.infer<typeof institutionFuturesReportingSchema>;

export type InstitutionFuturesReportingOrDefaults = InstitutionFuturesReporting | Partial<InstitutionFuturesReporting>;

export const institutionFuturesReportingGeneralSchema = z
  .object({
    mifid: z.enum(BINARY_ANSWERS, {
      error: i18n.t('institutionDetails.reporting.errors.mifid'),
    }),
  })
  .and(
    z
      .discriminatedUnion(
        'emir',
        [
          z.object({
            emir: z.literal('YES'),
            emirReportingResponsibility: z.enum(BINARY_ANSWERS, {
              error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirReportingResponsibility'),
            }),
          }),
          z.object({
            emir: z.literal('NO'),
          }),
        ],
        {
          error: i18n.t('institutionDetails.reporting.errors.emir'),
        },
      )
      .and(
        z.discriminatedUnion(
          'emirClassification',
          [
            z.object({
              emirClassification: z.literal(EmirClassification.FINANCIAL_COUNTERPARTY),
              esmaClassification: z.enum(Object.values(EsmaClassification), {
                error: i18n.t('institutionDetails.reporting.emirDetails.errors.esmaClassification'),
              }),
            }),
            z.object({
              emirClassification: z.literal(EmirClassification.NON_FINANCIAL_COUNTERPARTY),
              naceClassification: z.enum(Object.values(NaceClassification), {
                error: i18n.t('institutionDetails.reporting.emirDetails.errors.naceClassification'),
              }),
              emirRiskReductionConfirmation: z.enum(EMIR_RISK_REDUCTION_CONFIRMATION_OPTIONS, {
                error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirRiskReductionConfirmation'),
              }),
            }),
            z.object({
              emirClassification: z.literal(EmirClassification.OTHER),
            }),
          ],
          {
            error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirClassification'),
          },
        ),
      )
      .and(
        z.object({
          emirThreshold: z.enum(Object.values(EmirThreshold), {
            error: i18n.t('institutionDetails.reporting.emirDetails.errors.emirThreshold'),
          }),
        }),
      ),
  );

export type InstitutionFuturesReportingGeneral = z.infer<typeof institutionFuturesReportingGeneralSchema>;

export const institutionFuturesReportingEmirSchema = institutionDetailsReportingEmirReportingResponsibilityNoSchema.pick({
  emirUtiAcknowledgment: true,
  emirPostTradeRiskConfirmation: true,
});

export type InstitutionFuturesReportingEmir = z.infer<typeof institutionFuturesReportingEmirSchema>;

export type InstitutionFuturesReportingEmirOrDefaults = InstitutionFuturesReportingEmir | Partial<InstitutionFuturesReportingEmir>;
